import { useState, useCallback } from 'react';
import { RENDER_TYPE } from '../types';

interface UsePdfPageScaleOptions {
    scale: number;
    renderScale: number | undefined;
    renderType: RENDER_TYPE;
    pageNum: number;
}

export interface UsePdfPageScaleReturn {
    /** 传给 usePdfRenderer 的实际渲染 scale（Canvas 模式为 renderScale，其他模式为 scale） */
    effectiveRenderScale: number;
    /** 传给 .pdf-page-container 的 CSS transform 比例（= scale / canvasReadyAtScale） */
    cssTransformRatio: number;
    /** cssTransformRatio 是否需要应用 transform（偏差 > 0.001 才应用，避免浮点噪声） */
    needsTransform: boolean;
    /**
     * canvas 完成新 tier 渲染后调用，传入本次实际渲染的 scale。
     * 内部更新 canvasReadyAtScale，使 CSS transform 回归 1，消除补偿。
     */
    notifyRenderComplete: (renderedAtScale: number) => void;
}

/**
 * PDF 页面 Scale 管理 Hook
 *
 * 封装 PdfPage 内与 scale 相关的三个概念，消除 PdfPage 与 usePdfViewport 的环形依赖：
 * - effectiveRenderScale：canvas 应以哪个 scale 渲染（tier 决策由 usePdfViewport 完成）
 * - canvasReadyAtScale：canvas 上次成功渲染时的 scale（离屏渲染完成前保持旧值，视觉连续）
 * - cssTransformRatio：当前视觉 scale 与 canvas 实际 scale 的差值，通过 CSS transform 补偿
 */
export function usePdfPageScale(options: UsePdfPageScaleOptions): UsePdfPageScaleReturn {
    const { scale, renderScale, renderType, pageNum } = options;

    // Canvas 模式使用 renderScale（tier 基准），其他模式直接用 scale
    const effectiveRenderScale =
        renderType === RENDER_TYPE.CANVAS && renderScale && renderScale > 0 ? renderScale : scale;

    // 用 (readyPageNum, readyScale) 配对状态追踪"canvas 上次成功渲染的页码与 scale"。
    // 页码变化时无需额外 effect 重置——只要 readyPageNum !== pageNum，canvasReadyAtScale 自动归零。
    // 避免了 useEffect 异步重置在首帧仍读到旧 scale 的时序问题。
    const [readyState, setReadyState] = useState<{ pageNum: number; scale: number } | null>(null);

    const notifyRenderComplete = useCallback(
        (renderedAtScale: number) => {
            if (renderType === RENDER_TYPE.CANVAS) {
                setReadyState({ pageNum, scale: renderedAtScale });
            }
        },
        [renderType, pageNum],
    );

    // 仅当记录的页码与当前页一致时，readyScale 才有效；否则视为 0（触发退化逻辑）
    const canvasReadyAtScale = readyState?.pageNum === pageNum ? readyState.scale : 0;

    // 用 canvasReadyAtScale 计算补偿比例；为 0 时退化为 effectiveRenderScale
    const displayRenderScale =
        renderType === RENDER_TYPE.CANVAS && canvasReadyAtScale > 0
            ? canvasReadyAtScale
            : effectiveRenderScale;
    const cssTransformRatio = displayRenderScale > 0 ? scale / displayRenderScale : 1;
    const needsTransform = Math.abs(cssTransformRatio - 1) > 0.001;

    return { effectiveRenderScale, cssTransformRatio, needsTransform, notifyRenderComplete };
}
